/**
 * Seguimiento de los exports en vuelo.
 *
 * Antes esto eran dos slots unicos en localStorage, __current_export_id__ y
 * __last_processed_export_id__, y triggerExport limpiaba el primero. Con eso,
 * pedir un segundo export perdia el rastro del primero para la descarga
 * automatica, y dos pestanas abiertas se pisaban entre si.
 *
 * Ahora es un mapa id -> {procesado}. Varios exports pueden estar en vuelo a la
 * vez y cada uno lleva su propia marca de procesado.
 */
const STORAGE_KEY = '__tracked_exports__';

type TrackedExport = { processed: boolean };
type TrackedExports = Record<string, TrackedExport>;

/** Cache en memoria para no leer y parsear en cada comprobacion. */
let cache: TrackedExports | null = null;

const read = (): TrackedExports => {
  if (cache) return cache;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    cache = raw ? (JSON.parse(raw) as TrackedExports) : {};
  } catch (error) {
    // Ventana privada, storage bloqueado o JSON corrupto: se sigue en memoria.
    console.warn('No se pudo leer el seguimiento de exports:', error);
    cache = {};
  }
  return cache;
};

const write = (value: TrackedExports): void => {
  cache = value;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch (error) {
    // Se pierde al recargar, pero la sesion actual sigue funcionando.
    console.warn('No se pudo guardar el seguimiento de exports:', error);
  }
};

/** Empieza a seguir un export. Conserva su marca de procesado si ya existia. */
export const trackExport = (id: string): void => {
  if (!id) return;
  const actual = read();
  write({ ...actual, [id]: { processed: actual[id]?.processed ?? false } });
};

export const isTracked = (id: string): boolean => Boolean(id) && id in read();

/**
 * Marca un export como ya atendido, para no descargarlo dos veces si el aviso
 * llega por los dos canales -WebSocket y polling-.
 */
export const markProcessed = (id: string): void => {
  if (!id) return;
  const actual = read();
  write({ ...actual, [id]: { processed: true } });
};

export const wasProcessed = (id: string): boolean => Boolean(read()[id]?.processed);

/** Deja de seguir UN export, sin tocar los demas. */
export const forgetTrackedExport = (id: string): void => {
  if (!id) return;
  const actual = read();
  if (!(id in actual)) return;
  const { [id]: _descartado, ...resto } = actual;
  write(resto);
};

/** Vacia el seguimiento en memoria. El storage queda como estaba. */
export const clearTrackedExports = (): void => {
  cache = null;
};
